import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { History, Tag, Sparkles, Bug, Wrench, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

interface VersionChange {
  category?: string;
  description?: string;
  type?: string;
  text?: string;
}

interface NormalizedChange {
  category: string;
  description: string;
}

interface AppVersion {
  id: string;
  version_number: string;
  release_date: string;
  changes: VersionChange[];
}

const categoryIcons: Record<string, React.ReactNode> = {
  Feature: <Sparkles className="h-3.5 w-3.5" />,
  Bugfix: <Bug className="h-3.5 w-3.5" />,
  Improvement: <Wrench className="h-3.5 w-3.5" />,
  Initial: <Zap className="h-3.5 w-3.5" />,
};

const categoryColors: Record<string, string> = {
  Feature: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  Bugfix: "bg-red-500/10 text-red-500 border-red-500/20",
  Improvement: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  Initial: "bg-primary/10 text-primary border-primary/20",
};

interface VersionDisplayProps {
  isCollapsed?: boolean;
}

const VersionDisplay = ({ isCollapsed = false }: VersionDisplayProps) => {
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [currentVersion, setCurrentVersion] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    const { data, error } = await supabase
      .from("app_versions")
      .select("*")
      .order("release_date", { ascending: false });

    if (!error && data) {
      const typedVersions = data.map((v) => ({
        ...v,
        changes: Array.isArray(v.changes) 
          ? (v.changes as unknown as VersionChange[])
          : [],
      }));
      setVersions(typedVersions);
      if (typedVersions.length > 0) {
        setCurrentVersion(typedVersions[0].version_number);
      }
    }
  };

  const getCategoryIcon = (category: string) => {
    return categoryIcons[category] || <Tag className="h-3.5 w-3.5" />;
  };

  const getCategoryColor = (category: string) => {
    return categoryColors[category] || "bg-muted text-muted-foreground border-border";
  };

  if (!currentVersion) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer",
            isCollapsed ? "justify-center px-2" : "px-4"
          )}
        >
          <History className="h-3 w-3" />
          {!isCollapsed && <span>v{currentVersion}</span>}
          {isCollapsed && <span className="sr-only">v{currentVersion}</span>}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Version History
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6">
            {versions.map((version, index) => (
              <div
                key={version.id}
                className={cn(
                  "relative pl-6 pb-6",
                  index !== versions.length - 1 && "border-l-2 border-border"
                )}
              >
                {/* Version marker */}
                <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-primary border-2 border-background" />
                
                {/* Version header */}
                <div className="flex items-center gap-3 mb-3">
                  <Badge variant="outline" className="font-mono text-sm">
                    v{version.version_number}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(version.release_date), "dd. MMMM yyyy", { locale: de })}
                  </span>
                  {index === 0 && (
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      Aktuell
                    </Badge>
                  )}
                </div>

                {/* Changes list */}
                <div className="space-y-2">
                  {version.changes.map((change, changeIndex) => {
                    // Normalisiere die Daten - unterstütze beide Formate
                    const category = change.category || change.type || 'Feature';
                    const description = change.description || change.text || '';
                    
                    // Kapitalisiere die Kategorie für konsistente Anzeige
                    const normalizedCategory = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
                    
                    if (!description) return null;
                    
                    return (
                      <div
                        key={changeIndex}
                        className="flex items-start gap-2 text-sm"
                      >
                        <Badge
                          variant="outline"
                          className={cn(
                            "flex items-center gap-1 text-xs px-2 py-0.5 shrink-0",
                            getCategoryColor(normalizedCategory)
                          )}
                        >
                          {getCategoryIcon(normalizedCategory)}
                          {normalizedCategory}
                        </Badge>
                        <span className="text-foreground">{description}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default VersionDisplay;
